import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AppMode, useAppMode } from '@/hooks/use-app-mode';
import { MagicWandIcon } from '@radix-ui/react-icons';
import { MessageSquare, Bot, ArrowRight, Sparkles } from 'lucide-react';
import { openAIService } from '@/lib/ai/openai-service';
import { useAuth } from '@/hooks/use-auth';
import { motion } from 'framer-motion';

export function ModeSelectionDialog() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<AppMode>('traditional');
  const { mode, setMode } = useAppMode();
  const { user } = useAuth();
  const [hasShownDialog, setHasShownDialog] = useState(false);

  // Initialize selectedMode based on current mode
  useEffect(() => {
    setSelectedMode(mode);
  }, [mode]);

  // Reset the flag when component mounts (temporary for testing)
  useEffect(() => {
    try {
      localStorage.removeItem('user-has-set-mode');
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
    }
  }, []);

  // Check if we need to show the dialog on login
  useEffect(() => {
    // Only show the dialog if:
    // 1. User is logged in
    // 2. We haven't shown the dialog in this session
    // 3. The user hasn't previously set a mode preference
    const hasUserSetMode = localStorage.getItem('user-has-set-mode') === 'true';
    
    if (user && !hasShownDialog && !hasUserSetMode) {
      setOpen(true);
      setHasShownDialog(true);
    }
  }, [user, hasShownDialog]);

  const handleModeSelect = (mode: AppMode) => {
    setSelectedMode(mode);
  };

  const handleSavePreference = () => {
    setMode(selectedMode);
    setOpen(false);
    
    // Mark that the user has set their mode preference
    try {
      localStorage.setItem('user-has-set-mode', 'true');
    } catch (error) {
      console.warn('Error saving mode preference to localStorage:', error);
    }

    // Navigate to the appropriate page based on the selected mode
    if (selectedMode === 'ai') {
      navigate('/flexhelp-ai');
    } else {
      navigate('/chat'); // Default for traditional mode
    }
  };

  const isAIAvailable = openAIService.isReady();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Choose Your Experience</DialogTitle>
          <DialogDescription>
            Welcome to FlexHelp! Select how you'd like to interact with the platform.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedMode === 'traditional' ? 'border-primary ring-2 ring-primary/20' : ''
            }`}
            onClick={() => handleModeSelect('traditional')}
          >
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="rounded-full bg-primary/10 p-3 mb-4">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">Traditional Mode</h3>
              <p className="text-sm text-muted-foreground">
                Classic interface with structured navigation, forms, and standard chat features.
              </p>
              
              {selectedMode === 'traditional' && (
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="mt-4 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full"
                >
                  Selected
                </motion.div>
              )}
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedMode === 'ai' ? 'border-primary ring-2 ring-primary/20' : ''
            } ${!isAIAvailable ? 'opacity-70 cursor-not-allowed' : ''}`}
            onClick={() => isAIAvailable && handleModeSelect('ai')}
          >
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="rounded-full bg-primary/10 p-3 mb-4">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-medium mb-2">FlexHelp AI</h3>
              <p className="text-sm text-muted-foreground">
                AI-powered interface with natural language interactions and smart suggestions.
              </p>
              
              {!isAIAvailable && (
                <p className="text-xs text-amber-500 mt-2">
                  AI features are currently unavailable
                </p>
              )}
              
              {selectedMode === 'ai' && (
                <motion.div 
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="mt-4 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full"
                >
                  Selected
                </motion.div>
              )}
            </CardContent>
          </Card>
        </div>

        <p className="text-xs text-muted-foreground">
          You can change your mode anytime using the mode selector at the top of the dashboard.
        </p>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Skip
          </Button>
          <Button onClick={handleSavePreference} className="gap-2">
            Continue with selected mode
            <ArrowRight className="h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 